import type { LucideIcon } from "lucide-react";
import {
  Banknote,
  BookOpen,
  Building,
  Building2,
  CalendarDays,
  CalendarRange,
  CarFront,
  LayoutDashboard,
  Car,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Coins,
  Database,
  FileCheck2,
  FileText,
  Fingerprint,
  FolderTree,
  HandCoins,
  IdCard,
  KeyRound,
  Layers,
  Landmark,
  ListTree,
  PackageOpen,
  Receipt,
  Scale,
  ScrollText,
  Server,
  Shield,
  SlidersHorizontal,
  Tag,
  Tags,
  MapPin,
  ShoppingCart,
  Timer,
  TrendingUp,
  Truck,
  Undo2,
  Users,
  Table,
} from "lucide-react";
import { LoanIcon } from "@/components/icons/LoanIcon";

export type AppNavLeafItem = {
  title: string;
  url: string;
  icon: LucideIcon;
  children?: AppNavLeafItem[];
};

export const dashboardNavItem: AppNavLeafItem = { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard };

export const productNavItem: AppNavLeafItem = { title: "Products (Cars)", url: "/products", icon: Car };
export const salesNavItem: AppNavLeafItem = { title: "Sales", url: "/product-sales", icon: Receipt };
export const saleReturnsNavItem: AppNavLeafItem = { title: "Sale Returns", url: "/sale-returns", icon: Undo2 };

export const catalogNavItems: AppNavLeafItem[] = [
  { title: "Categories", url: "/categories", icon: FolderTree },
  { title: "Brands", url: "/brands", icon: Tag },
  { title: "Model", url: "/vehicle-models", icon: CarFront },
  { title: "Version", url: "/vehicle-versions", icon: PackageOpen },
  { title: "Purchase cost categories", url: "/purchase-cost-categories", icon: Tags },
  { title: "Purchase cost types", url: "/purchase-cost-types", icon: Receipt },
  { title: "Attribute groups", url: "/attribute-groups", icon: Layers },
  { title: "Attributes", url: "/attributes", icon: ListTree },
  { title: "Location", url: "/ware-houses", icon: MapPin },
];

export const customerSupplierNavItems: AppNavLeafItem[] = [
  { title: "Customers", url: "/customers", icon: Users },
  { title: "Suppliers", url: "/suppliers", icon: Truck },
  { title: "Investors", url: "/investors", icon: HandCoins },
  { title: "Ledger", url: "/parties/ledger", icon: BookOpen },
];

export const accountsNavItems: AppNavLeafItem[] = [
  { title: "General Ledger", url: "/accounts/company-ledger", icon: ScrollText },
  { title: "Trial Balance", url: "/accounts/trial-balance", icon: Scale },
  { title: "Income Statement", url: "/accounts/income-statement", icon: TrendingUp },
  { title: "Balance Sheet", url: "/accounts/balance-sheet", icon: Landmark },
  { title: "Chart of Accounts", url: "/accounts/chart-of-accounts", icon: ListTree },
  { title: "Vouchers", url: "/accounts/vouchers", icon: FileText },
];

export const employeeOnlyNavItems: AppNavLeafItem[] = [
  { title: "Employees", url: "/employees", icon: Users },
  { title: "Employee Ledger", url: "/employees/ledger", icon: BookOpen },
  { title: "Departments", url: "/departments", icon: Building2 },
  { title: "Designations", url: "/designations", icon: IdCard },
];

export const leaveNavItems: AppNavLeafItem[] = [
  { title: "Leave period", url: "/leave-years", icon: CalendarRange },
  { title: "Leave types", url: "/leave-types", icon: CalendarDays },
  { title: "Leave policies", url: "/leave-policies", icon: ScrollText },
];

export const attendanceNavItems: AppNavLeafItem[] = [
  { title: "Work shifts", url: "/work-shifts", icon: Clock },
  { title: "Rosters", url: "/rosters", icon: Table },
  { title: "Working calendar", url: "/working-calendar", icon: CalendarDays },
  { title: "Daily attendance", url: "/attendance/daily", icon: ClipboardCheck },
  { title: "Punch log", url: "/attendance/punches", icon: Fingerprint },
  { title: "Attendance Adjustment", url: "/attendance/adjustments", icon: ClipboardList },
  { title: "Attendance rules", url: "/attendance/rules", icon: SlidersHorizontal },
];

export const expenseNavItems: AppNavLeafItem[] = [
  { title: "Expense categories", url: "/expense-categories", icon: Tags },
  { title: "Expenses", url: "/expenses", icon: Receipt },
];

export const dataBankNavItems: AppNavLeafItem[] = [
  { title: "Banks", url: "/banks", icon: Building2 },
  { title: "Currencies", url: "/currencies", icon: Coins },
];

/** Permission categories, Permissions, Roles — not companies. */
export const accessPermissionsNavItems: AppNavLeafItem[] = [
  { title: "Permission categories", url: "/permission-categories", icon: Layers },
  { title: "Permissions", url: "/permissions", icon: KeyRound },
  { title: "Roles", url: "/roles", icon: Shield },
];

export const companyNavItems: AppNavLeafItem[] = [
  { title: "Companies", url: "/companies", icon: Building },
  { title: "Biometric devices", url: "/biometric-devices", icon: Fingerprint },
  { title: "Company devices", url: "/company-biometric-devices", icon: Server },
];

export const workPoliciesNavItem: AppNavLeafItem = {
  title: "Work policies",
  url: "/work-policies",
  icon: FileCheck2,
};

export const leaveRequestsNavItem: AppNavLeafItem = {
  title: "Leave requests",
  url: "/leave-requests",
  icon: ClipboardList,
};

export const payrollNavItems: AppNavLeafItem[] = [
  { title: "Salary policies", url: "/salary-policies", icon: Banknote },
  { title: "Allowance Types", url: "/employee-allowance-types", icon: Coins },
  { title: "Deduction Types", url: "/employee-deduction-types", icon: Coins },
  { title: "Attendances", url: "/payroll/attendances", icon: ClipboardCheck },
  { title: "Salaries", url: "/payroll/salary-batches", icon: Layers },
  { title: "Salary increments", url: "/payroll/salary-increments", icon: TrendingUp },
  { title: "Employee loans", url: "/payroll/loans", icon: LoanIcon },
  { title: "Advance salary", url: "/payroll/salary-advances", icon: HandCoins },
  { title: "Salary adjustments", url: "/payroll/salary-adjustments", icon: SlidersHorizontal },
];

/** Top-level app modules (sidebar: one collapsible group each with nested leaf links). */
export type AppNavModuleMeta = {
  id: string;
  title: string;
  icon: LucideIcon;
};

export const APP_NAV_MODULE_META: AppNavModuleMeta[] = [
  { id: "products-inventory", title: "Products & Inventory", icon: Layers },
  { id: "sales", title: "Sales", icon: ShoppingCart },
  { id: "customer-supplier", title: "Parties", icon: Users },
  { id: "accounts", title: "Accounts", icon: BookOpen },
  { id: "employee", title: "Employees", icon: IdCard },
  { id: "leave", title: "Leave", icon: CalendarRange },
  { id: "payroll", title: "Payroll", icon: Banknote },
  { id: "attendance", title: "Attendance", icon: Timer },
  { id: "expense", title: "Expenses", icon: Receipt },
  { id: "data-bank", title: "Data Bank", icon: Database },
  { id: "access", title: "Access & Permissions", icon: Shield },
  { id: "company", title: "Companies", icon: Building },
];

export function pathMatchesNavLeaf(pathname: string, url: string): boolean {
  return pathname === url || pathname.startsWith(url + "/");
}

export function navItemMatchesPath(pathname: string, item: AppNavLeafItem): boolean {
  if (pathMatchesNavLeaf(pathname, item.url)) return true;
  return Boolean(item.children?.some((child) => navItemMatchesPath(pathname, child)));
}
